import { useEffect, useMemo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import $api from 'http'
import Notification from 'utils/Notifications'
import {
  Icon,
  PageHeader,
  Skeleton,
  EmptyState,
  ErrorState,
  Card,
  Badge,
  UnvButton,
  UnvModal,
} from 'components'

import styles from './SchedulePage.module.scss'

const TIME_SLOTS = ['8:00', '9:30', '11:00', '12:30', '14:30', '16:00', '17:30', '19:00']
// Wave Final (P1-I4): WEEKDAY_SHORT перенесены в i18n, см. weekdayLabels()
// внутри компонента — ключи common.weekday_short_{1..6}.
const WEEKDAY_KEYS = [
  ['common.weekday_short_mon', 'Пн'],
  ['common.weekday_short_tue', 'Вт'],
  ['common.weekday_short_wed', 'Ср'],
  ['common.weekday_short_thu', 'Чт'],
  ['common.weekday_short_fri', 'Пт'],
  ['common.weekday_short_sat', 'Сб'],
]

const isoDate = (d) => {
  const dt = new Date(d)
  const y = dt.getFullYear()
  const m = String(dt.getMonth() + 1).padStart(2, '0')
  const day = String(dt.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
const startOfWeek = (date) => {
  const d = new Date(date)
  const day = d.getDay() || 7
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - day + 1)
  return d
}
const addDays = (date, n) => {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}
const fmtDate = (d) => {
  const day = String(d.getDate()).padStart(2, '0')
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${day}.${m}`
}

const SchedulePage = () => {
  const { t } = useTranslation('common')
  const [view, setView] = useState('mine') // mine | group | teacher | room
  const [anchor, setAnchor] = useState(new Date())
  const [me, setMe] = useState(null)
  const [lessons, setLessons] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [filterValue, setFilterValue] = useState('')
  const [selected, setSelected] = useState(null)
  const [now, setNow] = useState(new Date())
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })
  const touchStart = useRef({ x: 0, y: 0, t: 0 })

  // tick clock every minute for now-line
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(id)
  }, [])

  const range = useMemo(() => {
    const s = startOfWeek(anchor)
    return { from: isoDate(s), to: isoDate(addDays(s, 6)) }
  }, [anchor])

  const weekDates = useMemo(() => {
    const s = startOfWeek(anchor)
    return Array.from({ length: 6 }, (_, i) => addDays(s, i))
  }, [anchor])

  const todayKey = useMemo(() => {
    const t0 = new Date()
    const day = t0.getDay() || 7
    return day <= 6 ? day : 0
  }, [])

  const loadMe = async () => {
    try {
      const r = await $api.get('/admin-panel/me/')
      setMe(r.data)
    } catch {
      setMe(null)
    }
  }

  const loadLessons = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const params = { date_from: range.from, date_to: range.to }
      if (view === 'group' && filterValue) params.group = filterValue
      if (view === 'teacher' && filterValue) params.teacher = filterValue
      if (view === 'room' && filterValue) params.room = filterValue
      if (view === 'mine' && me?.id) params.user = me.id
      const r = await $api.get('/academic/lessons/', { params })
      setLessons(r.data?.results || r.data || [])
    } catch {
      setLoadError(true)
      setNotify({ isOpen: true, message: t('schedule.load_failed', 'Не удалось загрузить'), type: 'error', sound: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadMe() }, [])
  useEffect(() => { loadLessons() /* eslint-disable-next-line */ }, [anchor, view, filterValue, me])

  // map: dayIdx -> array of lessons with start time
  const byDay = useMemo(() => {
    const m = {}
    for (let i = 1; i <= 6; i++) m[i] = []
    lessons.forEach((l) => {
      const dStr = l.date || (l.start_at ? l.start_at.slice(0, 10) : '')
      if (!dStr) return
      const d = new Date(dStr)
      const day = d.getDay() || 7
      if (day <= 6) m[day].push(l)
    })
    return m
  }, [lessons])

  const navigate = (dir) => setAnchor(addDays(anchor, dir * 7))

  // mobile swipe
  const onTouchStart = (e) => {
    const t0 = e.touches[0]
    touchStart.current = { x: t0.clientX, y: t0.clientY, t: Date.now() }
  }
  const onTouchEnd = (e) => {
    const t0 = e.changedTouches[0]
    const dx = t0.clientX - touchStart.current.x
    const dy = t0.clientY - touchStart.current.y
    const dt = Date.now() - touchStart.current.t
    if (dt < 500 && Math.abs(dx) > 80 && Math.abs(dy) < 50) navigate(dx < 0 ? 1 : -1)
  }

  const renderLessonCard = (l) => (
    <button
      key={l.id || `${l.subject}-${l.start_at}`}
      type="button"
      className={styles.lessonCard}
      onClick={() => setSelected(l)}
      aria-label={`${l.subject_name || ''} ${l.teacher_name || ''}`}
    >
      <div className={styles.lessonTime}>
        {l.start_at?.slice(11, 16) || '—'}
        <span className={styles.lessonDuration}>{l.duration_minutes ? `${l.duration_minutes} мин` : ''}</span>
      </div>
      <div className={styles.lessonSubject}>{l.subject_name || l.subject || '—'}</div>
      <div className={styles.lessonMeta}>
        <span><Icon name="user" size={12} /> {l.teacher_name || '—'}</span>
        <span><Icon name="building" size={12} /> {l.room || l.classroom || '—'}</span>
      </div>
      {l.lesson_type && <Badge variant="accent" size="sm">{l.lesson_type}</Badge>}
    </button>
  )

  // now-line offset (px) within today's column based on time of day
  const nowLineTop = useMemo(() => {
    if (!now) return null
    const start = 8 * 60 // 08:00
    const end = 20 * 60 + 20 // 20:20
    const cur = now.getHours() * 60 + now.getMinutes()
    if (cur < start || cur > end) return null
    const ratio = (cur - start) / (end - start)
    return ratio
  }, [now])

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('schedule.title', 'Расписание')}
        subtitle={t('schedule.subtitle', 'Группы, аудитории, нагрузка преподавателей')}
        actions={
          <UnvButton variant="ghost" iconLeft="download" onClick={() => window.print()}>
            {t('schedule.print', 'Печать / PDF')}
          </UnvButton>
        }
      />

      <div className={styles.toolbar} role="toolbar" aria-label={t('schedule.toolbar', 'Управление видом')}>
        <div className={styles.viewSwitch} role="tablist">
          {[
            { key: 'mine', label: t('schedule.view_mine', 'Моё расписание'), icon: 'user' },
            { key: 'group', label: t('schedule.view_group', 'Группа'), icon: 'group' },
            { key: 'teacher', label: t('schedule.view_teacher', 'Преподаватель'), icon: 'teacher' },
            { key: 'room', label: t('schedule.view_room', 'Аудитория'), icon: 'building' },
          ].map((v) => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={view === v.key}
              className={[styles.viewBtn, view === v.key ? styles.viewBtnActive : ''].join(' ')}
              onClick={() => { setView(v.key); setFilterValue('') }}
            >
              <Icon name={v.icon} size={14} /> {v.label}
            </button>
          ))}
        </div>

        {view !== 'mine' && (
          <input
            className={styles.filterInput}
            placeholder={t('schedule.filter_placeholder', 'Введите имя/номер...')}
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            aria-label={t('schedule.filter_placeholder', 'Введите имя/номер...')}
          />
        )}

        <div className={styles.spacer} />

        <button type="button" className={styles.iconBtn} onClick={() => navigate(-1)} aria-label={t('schedule.prev_week', 'Прошлая неделя')}>
          <Icon name="chevron-left" size={16} />
        </button>
        <span className={styles.weekRange}>{fmtDate(weekDates[0])} — {fmtDate(weekDates[5])}</span>
        <button type="button" className={styles.iconBtn} onClick={() => navigate(1)} aria-label={t('schedule.next_week', 'Следующая неделя')}>
          <Icon name="chevron-right" size={16} />
        </button>
        <UnvButton size="sm" variant="ghost" onClick={() => setAnchor(new Date())}>{t('schedule.today', 'Сегодня')}</UnvButton>
      </div>

      {loading ? (
        <Card><Skeleton variant="table" rows={8} cols={7} /></Card>
      ) : loadError ? (
        <ErrorState title={t('schedule.load_failed', 'Не удалось загрузить')} onRetry={loadLessons} />
      ) : lessons.length === 0 ? (
        <Card>
          <EmptyState
            iconName="calendar"
            title={t('schedule.no_data', 'Нет занятий')}
            description={t('schedule.no_data_desc', 'Попробуйте изменить фильтры или диапазон дат')}
          />
        </Card>
      ) : (
        <Card padding="none" className={styles.gridCard}>
          <div
            className={styles.grid}
            role="grid"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div className={styles.gridHeaderCorner} />
            {WEEKDAY_KEYS.map(([key, fb], i) => (
              <div
                key={key}
                className={[styles.gridHeaderDay, i + 1 === todayKey ? styles.gridHeaderToday : ''].join(' ')}
                role="columnheader"
              >
                <div className={styles.dayLabel}>{t(key, fb)}</div>
                <div className={styles.dayDate}>{fmtDate(weekDates[i])}</div>
              </div>
            ))}

            {TIME_SLOTS.map((tt, idx) => (
              <div
                key={tt}
                className={styles.gridTimeCol}
                role="rowheader"
                style={{ gridRow: idx + 2 }}
              >
                <span className={styles.timeStart}>{tt}</span>
              </div>
            ))}

            {WEEKDAY_KEYS.map(([k], dIdx) => (
              <div
                key={k}
                className={[styles.gridDayCol, dIdx + 1 === todayKey ? styles.gridDayColToday : ''].join(' ')}
                style={{ gridColumn: dIdx + 2, gridRow: '2 / span 8' }}
              >
                {dIdx + 1 === todayKey && nowLineTop != null && (
                  <div
                    className={styles.nowLine}
                    style={{ top: `${nowLineTop * 100}%` }}
                    aria-label={t('schedule.now', 'Сейчас')}
                  />
                )}
                {(byDay[dIdx + 1] || []).map(renderLessonCard)}
              </div>
            ))}
          </div>
        </Card>
      )}

      <UnvModal
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.subject_name || t('schedule.lesson', 'Занятие')}
      >
        {selected && (
          <div className={styles.detail}>
            <div className={styles.detailRow}><Icon name="calendar" size={14} /> {selected.date || selected.start_at?.slice(0, 10)}</div>
            <div className={styles.detailRow}><Icon name="clock" size={14} /> {selected.start_at?.slice(11, 16)} — {selected.end_at?.slice(11, 16) || ''}</div>
            <div className={styles.detailRow}><Icon name="user" size={14} /> {selected.teacher_name || '—'}</div>
            <div className={styles.detailRow}><Icon name="building" size={14} /> {selected.room || selected.classroom || '—'}</div>
            {selected.group_name && <div className={styles.detailRow}><Icon name="group" size={14} /> {selected.group_name}</div>}
            {selected.note && <div className={styles.detailRow}><Icon name="info" size={14} /> {selected.note}</div>}
          </div>
        )}
        <UnvModal.Footer>
          <UnvButton variant="ghost" onClick={() => setSelected(null)}>{t('common.close', 'Закрыть')}</UnvButton>
        </UnvModal.Footer>
      </UnvModal>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default SchedulePage
